/**
 * MODIFIED FROM SHADCN ORIGINAL
 *
 * This component has been customized from the original shadcn/ui version.
 * Modifications: Added CVA variants (success, failure, warning, caution, info, subtle); Added asChild prop
 *
 * Original: https://ui.shadcn.com/
 */

import { cn } from '@ophelos/ui/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const badgeVariants = cva(
  'inline-flex items-center justify-center border py-0.5 font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      shape: {
        default: 'rounded-full',
        square: 'rounded-sm',
      },
      size: {
        default: 'px-2 text-xs',
        sm: 'h-4 min-w-4 px-1 text-[10px]',
        number: 'h-4 min-w-4 px-1 text-[10px] tabular-nums',
      },
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary: 'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        accent: 'border-ring/15 bg-accent text-accent-foreground [a&]:hover:bg-accent/90',
        destructive:
          'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline: 'bg-card text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        success: 'highlight-green',
        failure: 'highlight-red',
        warning: 'highlight-amber',
        caution: 'highlight-orange',
        info: 'highlight-indigo',
        subtle: 'bg-muted border border-border-light dark:bg-muted dark:border-border',
        subdued: 'bg-card border-border-light/75 text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
      },
    },
    defaultVariants: {
      shape: 'default',
      size: 'default',
      variant: 'default',
    },
  }
);

const Badge = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }>(
  ({ className, variant, size, shape, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';

    const wrapped = React.Children.map(children, (child) =>
      typeof child === 'string' || typeof child === 'number' ? <span>{child}</span> : child
    );

    return (
      <Comp ref={ref} data-slot="badge" className={cn(badgeVariants({ variant, size, shape }), className)} {...props}>
        {wrapped}
      </Comp>
    );
  }
);
Badge.displayName = 'Badge';

export { Badge, badgeVariants };
